import { Wind as WindIcon, Flame } from 'lucide-react';
import type { AirPayload, AqiCategory } from '../types';

const CATEGORY_STYLE: Record<AqiCategory, { bg: string; label: string; short: string }> = {
  good: { bg: 'bg-emerald-500/30 border-emerald-400/50', label: 'Good', short: 'Good' },
  moderate: {
    bg: 'bg-yellow-500/30 border-yellow-400/50 text-yellow-50',
    label: 'Moderate',
    short: 'Moderate',
  },
  'unhealthy-sensitive': {
    bg: 'bg-orange-500/30 border-orange-400/60 text-orange-50',
    label: 'Unhealthy for sensitive groups',
    short: 'USG',
  },
  unhealthy: {
    bg: 'bg-red-500/40 border-red-400/60 text-red-50',
    label: 'Unhealthy',
    short: 'Unhealthy',
  },
  'very-unhealthy': {
    bg: 'bg-purple-500/40 border-purple-400/60 text-purple-50',
    label: 'Very unhealthy',
    short: 'Very unhealthy',
  },
  hazardous: {
    bg: 'bg-rose-800/70 border-rose-600/60 text-rose-50',
    label: 'Hazardous',
    short: 'Hazardous',
  },
};

export default function AirQuality({ air }: { air: AirPayload }) {
  const cat = air.current.category;
  const style = cat ? CATEGORY_STYLE[cat] : null;

  return (
    <section>
      <h2 className="text-xs uppercase tracking-wider opacity-70 mb-2 flex items-center gap-2">
        <WindIcon className="w-3.5 h-3.5" /> Air quality
      </h2>
      <div
        className={`rounded-lg p-3 border backdrop-blur-sm ${
          style?.bg ?? 'bg-white/10 border-white/10'
        }`}
      >
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-3xl font-light tabular-nums">
              {air.current.aqi}
              <span className="text-sm opacity-70 ml-1">AQI</span>
            </div>
            <div className="text-sm">{style?.label ?? 'Unavailable'}</div>
          </div>
          <div className="text-xs text-right space-y-0.5">
            <div>
              PM2.5 <span className="tabular-nums font-medium">{air.current.pm25}</span>{' '}
              <span className="opacity-60">µg/m³</span>
            </div>
            <div>
              PM10 <span className="tabular-nums font-medium">{air.current.pm10}</span>
            </div>
            {air.current.ozone != null && (
              <div>
                O₃ <span className="tabular-nums font-medium">{Math.round(air.current.ozone)}</span>
              </div>
            )}
          </div>
        </div>
        {air.smokeRisk !== 'none' && (
          <div className="mt-2 flex items-center gap-2 text-xs border-t border-white/20 pt-2">
            <Flame className="w-3.5 h-3.5 text-amber-300" />
            {air.smokeRisk === 'likely'
              ? 'Wildfire smoke likely — limit outdoor exertion'
              : 'Possible smoke or regional haze — PM2.5 elevated'}
          </div>
        )}
      </div>
    </section>
  );
}
